// ===========================================
// Class Piece Progress Modal
// ===========================================
// Shows all enrolled students with their stage progress for a class piece
// Teachers can click stage segments to cycle status

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Alert,
  Chip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PictureAsPdfIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import StageProgressBar from './StageProgressBar';
import type { StageSegment } from './StageProgressBar';
import type { LessonPiece, SkillStatus } from '../../types/gamification.types';
import {
  useClassPieceProgress,
  useUpdateSkillProgress,
  useStartSkill,
  useCompleteSkill,
} from '../../hooks/useGamification';

// ===========================================
// TYPES
// ===========================================

interface ClassPieceProgressModalProps {
  open: boolean;
  onClose: () => void;
  lessonId: string;
  lessonName: string;
  lessonPieces: LessonPiece[];
}

// ===========================================
// COMPONENT
// ===========================================

export default function ClassPieceProgressModal({
  open,
  onClose,
  lessonId,
  lessonName,
  lessonPieces,
}: ClassPieceProgressModalProps) {
  const [selectedPieceIndex, setSelectedPieceIndex] = useState(0);

  const selectedPiece = lessonPieces[selectedPieceIndex];
  const selectedPieceId = selectedPiece?.piece?.id || '';

  const { data, isLoading } = useClassPieceProgress(
    open ? lessonId : '',
    open ? selectedPieceId : ''
  );

  const updateMutation = useUpdateSkillProgress();
  const startMutation = useStartSkill();
  const completeMutation = useCompleteSkill();

  const handleStageClick = (stage: StageSegment) => {
    if (!stage.skillProgressId) return;

    const nextStatus: Record<SkillStatus, SkillStatus> = {
      NOT_STARTED: 'IN_PROGRESS',
      IN_PROGRESS: 'COMPLETED',
      COMPLETED: 'NOT_STARTED',
    };

    const newStatus = nextStatus[stage.status];

    if (newStatus === 'IN_PROGRESS') {
      startMutation.mutate(stage.skillProgressId);
    } else if (newStatus === 'COMPLETED') {
      completeMutation.mutate({ skillProgressId: stage.skillProgressId });
    } else {
      updateMutation.mutate({
        skillProgressId: stage.skillProgressId,
        data: { status: 'NOT_STARTED' },
      });
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setSelectedPieceIndex(newIndex);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Piece Progress</Typography>
          <Typography variant="body2" color="text.secondary">
            {lessonName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Piece selector tabs (if multiple) */}
        {lessonPieces.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Tabs
              value={selectedPieceIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1 }}
            >
              {lessonPieces.map((lp) => (
                <Tab key={lp.id} label={lp.piece?.title || 'Untitled'} />
              ))}
            </Tabs>
            <Divider />
            {/* Lesson plan link for the selected piece */}
            {selectedPiece?.piece?.lessonPlanUrl && (
              <Box
                component="a"
                href={selectedPiece.piece.lessonPlanUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1,
                  color: 'error.main',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 14 }} />
                {selectedPiece.piece.lessonPlanName || 'Lesson Plan'}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Single piece title when only one piece */}
        {lessonPieces.length === 1 && selectedPiece?.piece && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedPiece.piece.title}
            </Typography>
            {selectedPiece.piece.composer && (
              <Typography variant="body2" color="text.secondary">
                {selectedPiece.piece.composer}
              </Typography>
            )}
            {/* Piece-level lesson plan link */}
            {selectedPiece.piece.lessonPlanUrl && (
              <Box
                component="a"
                href={selectedPiece.piece.lessonPlanUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                  color: 'error.main',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 14 }} />
                {selectedPiece.piece.lessonPlanName || 'Lesson Plan'}
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Stage legend */}
        {data?.piece?.skills && data.piece.skills.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {data.piece.skills.map((skill) => (
              <Chip
                key={skill.id}
                label={skill.name}
                size="small"
                sx={{
                  backgroundColor: skill.color || '#4580E4',
                  color: '#fff',
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            ))}
          </Box>
        )}

        {/* Student list */}
        {isLoading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : !data || data.students.length === 0 ? (
          <Alert severity="info">No students enrolled in this class.</Alert>
        ) : (
          <List disablePadding>
            {data.students.map((studentData) => {
              const stages: StageSegment[] = data.piece.skills.map((skill) => {
                const progress = studentData.skillProgress.find(
                  (sp) => sp.pieceSkillId === skill.id
                );
                return {
                  id: skill.id,
                  name: skill.name,
                  color: skill.color,
                  status: (progress?.status || 'NOT_STARTED') as SkillStatus,
                  skillProgressId: progress?.id,
                };
              });

              const completedCount = stages.filter((s) => s.status === 'COMPLETED').length;
              const isAllComplete = completedCount === stages.length && stages.length > 0;

              return (
                <ListItem
                  key={studentData.student.id}
                  sx={{
                    border: '1px solid',
                    borderColor: isAllComplete ? 'success.light' : 'divider',
                    borderRadius: 1,
                    mb: 0.5,
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {studentData.student.firstName} {studentData.student.lastName}
                          </Typography>
                          {isAllComplete && (
                            <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {completedCount}/{stages.length} stages
                        </Typography>
                      }
                      sx={{ my: 0 }}
                    />
                  </Box>
                  <StageProgressBar
                    stages={stages}
                    size="medium"
                    onStageClick={handleStageClick}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
